import { useMemo } from 'react';
import { Star } from 'lucide-react';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import type { BuildPlanResponse } from '@/types/api';
import { PlanFeed } from './PlanFeed';
import { LevelMark } from './PlanRowView';
import { planCounts, planRows } from './planRows';

function Chip({ children }: { children: string }) {
  return (
    <span className='rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium capitalize text-foreground'>
      {children}
    </span>
  );
}

function PlanHeader({ plan }: { plan: BuildPlanResponse }) {
  return (
    <header className='shrink-0 border-b border-border px-5 pb-3 pt-4'>
      <h3
        id='build-plan-title'
        className='font-mono text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground'
      >
        Your lesson plan
      </h3>
      {plan.summary && (
        <InlineMarkdown className='mt-2 line-clamp-3 block text-sm leading-relaxed text-foreground'>
          {plan.summary}
        </InlineMarkdown>
      )}
      <div className='mt-3 flex flex-wrap items-center gap-2'>
        {plan.subject && <Chip>{plan.subject}</Chip>}
        {plan.audience_level && <Chip>{plan.audience_level}</Chip>}
        <span className='font-mono text-xs text-muted-foreground'>
          {planCounts(plan)}
        </span>
      </div>
    </header>
  );
}

function Legend() {
  const item = 'flex items-center gap-1.5';
  return (
    <div className='flex shrink-0 items-center gap-3 text-xs text-muted-foreground'>
      <span className={item}>
        <LevelMark level='foundational' /> Foundational
      </span>
      <span className={item}>
        <LevelMark level='core' /> Core
      </span>
      <span className={item}>
        <LevelMark level='advanced' /> Advanced
      </span>
      <span className={item}>
        <Star aria-hidden className='h-3 w-3 fill-lesson text-lesson' /> Key
        idea
      </span>
    </div>
  );
}

function PlanFooter({ plan }: { plan: BuildPlanResponse }) {
  const setAside = plan.set_aside ?? 0;
  const examples = (plan.set_aside_examples ?? []).join('\n');
  return (
    <footer className='flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-border px-5 py-2.5'>
      <p
        className='text-xs text-muted-foreground'
        title={examples || undefined}
      >
        {setAside > 0
          ? `${setAside} key ${setAside === 1 ? 'point' : 'points'} set aside as outside your request`
          : 'Every key point is in the plan'}
      </p>
      <Legend />
    </footer>
  );
}

/**
 * "Your lesson plan": the outline the plan placed, shown while its concepts
 * are written up - summary, subject and level, then each section and its
 * concepts (level, key points, what they build on) appearing row by row,
 * and what was set aside. A card as tall as the screen leaves room for;
 * the page never scrolls.
 */
export function BuildPlanCard({ plan }: { plan: BuildPlanResponse }) {
  const rows = useMemo(() => planRows(plan), [plan]);
  return (
    <section
      aria-labelledby='build-plan-title'
      className='flex h-[clamp(16rem,calc(100dvh-30rem),38rem)] w-full flex-col overflow-hidden rounded-xl border border-border bg-card text-left shadow-sm'
    >
      <PlanHeader plan={plan} />
      <PlanFeed rows={rows} />
      <PlanFooter plan={plan} />
    </section>
  );
}
